/**
 * SubjectFormFields — bloque compartido para Representante / Apoderado /
 * Beneficiario Controlador.
 *
 * Reutilización: los 3 sujetos comparten ~70% de los campos de identidad
 * (nombre, fecha nac, CURP, RFC, INE, etc.). En lugar de duplicar el
 * markup, este componente recibe ``profile`` (objeto plano del sujeto),
 * ``onChange``, y banderas para activar bloques opcionales:
 *
 *  - ``includeContact``         REP/APO: teléfonos, email, ocupación
 *  - ``includeResidence``       REP/APO: tipo residencia, países
 *  - ``includePEP``             REP/APO: ¿es PEP?, vínculo PEP
 *  - ``includeOwnership``       BO: % directa, indirecta, cadena, tipo control
 *  - ``includeUIF``             BO: aviso UIF, riesgo, declaración
 *  - ``includeIdExpiry``        REP/APO: vigencia de la identificación
 *
 * Pre-fill por OCR: el padre recibe el ``ocrFields`` del sujeto (de
 * ``ocr_by_subject``) y lo merge con los campos editados por el usuario
 * ANTES de pasarlo aquí. Esa lógica vive en cada Phase para que el
 * componente quede puro y testeable.
 */
import React from "react";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";

const RESIDENCE_TYPES = [
  { value: "mexicano",               label: "Mexicano" },
  { value: "extranjero",             label: "Extranjero" },
  { value: "residencia_permanente",  label: "Residencia Permanente" },
  { value: "residencia_temporal",    label: "Residencia Temporal" },
];

const ID_TYPES = [
  { value: "INE",        label: "INE" },
  { value: "Pasaporte",  label: "Pasaporte" },
  { value: "Residencia", label: "Residencia" },
];

const CONTROL_TYPES = [
  { value: "uso_disfrute",       label: "Uso o disfrute / Beneficio económico" },
  { value: "control_directo",    label: "Control directo (≥25% acciones)" },
  { value: "control_indirecto",  label: "Control indirecto / Cadena accionaria" },
  { value: "administrador_unico", label: "Administrador único / Poder de decisión" },
];

const NATIONALITIES = ["Mexicana", "Estadounidense", "Canadiense", "Española", "Otra"];
const COUNTRIES = ["México", "Estados Unidos", "Canadá", "España", "Otro"];
const RISK_LEVELS = [
  { value: "bajo",  label: "Bajo" },
  { value: "medio", label: "Medio" },
  { value: "alto",  label: "Alto" },
];

export default function SubjectFormFields({
  profile = {},
  onChange,
  subjectKey = "subject",            // prefix for data-testids — unique per card
  includeContact = false,
  includeResidence = false,
  includePEP = false,
  includeOwnership = false,
  includeUIF = false,
  includeIdExpiry = false,
}) {
  const set = (field) => (e) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    onChange({ ...profile, [field]: value });
  };
  const tid = (suffix) => `${subjectKey}-${suffix}`;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="sm:col-span-2">
          <Label>Nombre completo *</Label>
          <Input
            data-testid={tid("full-name")}
            value={profile.full_name || ""}
            onChange={set("full_name")}
            placeholder="Nombre(s) Apellido Paterno Apellido Materno"
          />
        </div>

        <div>
          <Label>Fecha de nacimiento</Label>
          <Input type="date" data-testid={tid("birth-date")}
                 value={profile.birth_date || ""} onChange={set("birth_date")} />
        </div>

        <div>
          <Label>CURP</Label>
          <Input data-testid={tid("curp")} value={profile.curp || ""}
                 onChange={(e) => onChange({ ...profile, curp: e.target.value.toUpperCase() })}
                 maxLength={18} className="uppercase" />
        </div>

        <div>
          <Label>RFC</Label>
          <Input data-testid={tid("rfc")} value={profile.rfc || ""}
                 onChange={(e) => onChange({ ...profile, rfc: e.target.value.toUpperCase() })}
                 maxLength={13} className="uppercase" />
        </div>

        {includeContact && (
          <>
            <div>
              <Label>Email</Label>
              <Input type="email" data-testid={tid("email")}
                     value={profile.email || ""} onChange={set("email")} />
            </div>
            <div>
              <Label>Teléfono Celular</Label>
              <Input data-testid={tid("phone")} value={profile.phone || ""} onChange={set("phone")} />
            </div>
            <div>
              <Label>Teléfono de Oficina</Label>
              <Input data-testid={tid("office-phone")}
                     value={profile.office_phone || ""} onChange={set("office_phone")} />
            </div>
            <div>
              <Label>Ocupación</Label>
              <Input data-testid={tid("occupation")}
                     value={profile.occupation || ""} onChange={set("occupation")} />
            </div>
          </>
        )}

        {includeResidence && (
          <>
            <div>
              <Label>País de Nacimiento</Label>
              <Input data-testid={tid("birth-country")}
                     value={profile.birth_country || ""} onChange={set("birth_country")} />
            </div>
            <div>
              <Label>País de Nacionalidad</Label>
              <Input data-testid={tid("nationality-country")}
                     value={profile.nationality_country || ""} onChange={set("nationality_country")} />
            </div>
            <div>
              <Label>Tipo de Residencia</Label>
              <select
                data-testid={tid("residence-type")}
                className="w-full bg-slate-900 border border-slate-700 rounded-md px-3 py-2 text-sm text-white"
                value={profile.residence_type || ""}
                onChange={set("residence_type")}
              >
                <option value="">Selecciona…</option>
                {RESIDENCE_TYPES.map((r) => (
                  <option key={r.value} value={r.value}>{r.label}</option>
                ))}
              </select>
            </div>
          </>
        )}

        <div>
          <Label>Tipo de Identificación</Label>
          <select
            data-testid={tid("id-type")}
            className="w-full bg-slate-900 border border-slate-700 rounded-md px-3 py-2 text-sm text-white"
            value={profile.id_type || ""}
            onChange={set("id_type")}
          >
            <option value="">Selecciona…</option>
            {ID_TYPES.map((r) => (
              <option key={r.value} value={r.value}>{r.label}</option>
            ))}
          </select>
        </div>

        <div>
          <Label>Número de Identificación</Label>
          <Input data-testid={tid("id-number")}
                 value={profile.id_number || ""} onChange={set("id_number")} />
        </div>

        {includeIdExpiry && (
          <div>
            <Label>Vigencia de la Identificación</Label>
            <Input type="date" data-testid={tid("id-expiry")}
                   value={profile.id_expiry_date || ""} onChange={set("id_expiry_date")} />
          </div>
        )}

        {!includeOwnership && (
          <div>
            <Label>Nacionalidad</Label>
            <select
              data-testid={tid("nationality")}
              className="w-full bg-slate-900 border border-slate-700 rounded-md px-3 py-2 text-sm text-white"
              value={profile.nationality || ""}
              onChange={set("nationality")}
            >
              <option value="">Selecciona…</option>
              {NATIONALITIES.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
        )}
      </div>

      <div>
        <Label>Domicilio</Label>
        <Textarea data-testid={tid("address")} rows={2}
                  value={profile.address || ""} onChange={set("address")}
                  placeholder="Calle, número, colonia, ciudad, estado, CP, país" />
      </div>

      {includeOwnership && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2 border-t border-slate-800">
          <div className="sm:col-span-2">
            <Label>Tipo de Control *</Label>
            <select
              data-testid={tid("control-type")}
              className="w-full bg-slate-900 border border-slate-700 rounded-md px-3 py-2 text-sm text-white"
              value={profile.control_type || ""}
              onChange={set("control_type")}
            >
              <option value="">Selecciona…</option>
              {CONTROL_TYPES.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          </div>
          <div>
            <Label>% Participación Directa</Label>
            <Input type="number" min="0" max="100" step="0.01"
                   data-testid={tid("direct-pct")}
                   value={profile.direct_ownership_pct ?? ""} onChange={set("direct_ownership_pct")} />
          </div>
          <div>
            <Label>% Participación Indirecta</Label>
            <Input type="number" min="0" max="100" step="0.01"
                   data-testid={tid("indirect-pct")}
                   value={profile.indirect_ownership_pct ?? ""} onChange={set("indirect_ownership_pct")} />
          </div>
          <div className="sm:col-span-2">
            <Label>Cadena de Control</Label>
            <Textarea data-testid={tid("control-chain")} rows={2}
                      value={profile.control_chain || ""} onChange={set("control_chain")} />
          </div>
          <div>
            <Label>Nacionalidad</Label>
            <select
              data-testid={tid("nationality")}
              className="w-full bg-slate-900 border border-slate-700 rounded-md px-3 py-2 text-sm text-white"
              value={profile.nationality || ""}
              onChange={set("nationality")}
            >
              <option value="">Selecciona…</option>
              {NATIONALITIES.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          <div>
            <Label>País de Residencia Fiscal</Label>
            <select
              data-testid={tid("tax-residence")}
              className="w-full bg-slate-900 border border-slate-700 rounded-md px-3 py-2 text-sm text-white"
              value={profile.tax_residence_country || ""}
              onChange={set("tax_residence_country")}
            >
              <option value="">Selecciona…</option>
              {COUNTRIES.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-300 sm:col-span-2">
            <input type="checkbox" data-testid={tid("is-sole-admin")}
                   checked={!!profile.is_sole_administrator}
                   onChange={set("is_sole_administrator")} />
            ¿Es administrador único?
          </label>
        </div>
      )}

      {includePEP && (
        <div className="border-t border-slate-800 pt-3 space-y-3">
          <label className="flex items-center gap-2 text-sm text-slate-200">
            <input type="checkbox" data-testid={tid("is-pep")}
                   checked={!!profile.is_pep} onChange={set("is_pep")} />
            ¿Es persona políticamente expuesta (PEP)?
          </label>
          {profile.is_pep && (
            <div>
              <Label>Cargo o vínculo</Label>
              <Input data-testid={tid("pep-details")}
                     value={profile.pep_details || ""} onChange={set("pep_details")} />
            </div>
          )}
          <label className="flex items-center gap-2 text-sm text-slate-200">
            <input type="checkbox" data-testid={tid("is-pep-related")}
                   checked={!!profile.is_pep_related} onChange={set("is_pep_related")} />
            ¿Está relacionada con alguna PEP?
          </label>
          {profile.is_pep_related && (
            <div>
              <Label>Cargo o vínculo (relacionado)</Label>
              <Input data-testid={tid("pep-related-details")}
                     value={profile.pep_related_details || ""} onChange={set("pep_related_details")} />
            </div>
          )}
        </div>
      )}

      {includeUIF && (
        <div className="border-t border-slate-800 pt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="flex items-center gap-2 text-sm text-slate-300 sm:col-span-2">
            <input type="checkbox" data-testid={tid("is-pep-bo")}
                   checked={!!profile.is_pep} onChange={set("is_pep")} />
            ¿Es PEP?
          </label>
          {profile.is_pep && (
            <div className="sm:col-span-2">
              <Label>Cargo / Institución PEP</Label>
              <Input data-testid={tid("pep-position")}
                     value={profile.pep_position || ""} onChange={set("pep_position")} />
            </div>
          )}
          <div>
            <Label>Nivel de Riesgo</Label>
            <select
              data-testid={tid("risk-level")}
              className="w-full bg-slate-900 border border-slate-700 rounded-md px-3 py-2 text-sm text-white"
              value={profile.risk_level || ""}
              onChange={set("risk_level")}
            >
              <option value="">Selecciona…</option>
              {RISK_LEVELS.map((r) => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
          </div>
          <div>
            <Label>Aviso UIF #</Label>
            <Input data-testid={tid("uif-num")}
                   value={profile.uif_notice_number || ""} onChange={set("uif_notice_number")} />
          </div>
          <div>
            <Label>Fecha del aviso UIF</Label>
            <Input type="date" data-testid={tid("uif-date")}
                   value={profile.uif_notice_date || ""} onChange={set("uif_notice_date")} />
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-300 sm:col-span-2">
            <input type="checkbox" data-testid={tid("sworn-declaration")}
                   checked={!!profile.sworn_declaration} onChange={set("sworn_declaration")} />
            Declaración bajo protesta
          </label>
          <div>
            <Label>Fecha de declaración</Label>
            <Input type="date" data-testid={tid("declaration-date")}
                   value={profile.declaration_date || ""} onChange={set("declaration_date")} />
          </div>
          <div>
            <Label>Última actualización</Label>
            <Input type="date" data-testid={tid("last-update-date")}
                   value={profile.last_update_date || ""} onChange={set("last_update_date")} />
          </div>
        </div>
      )}
    </div>
  );
}
